import { cloneElement, useState, ReactElement, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import {
  AppBar as MuiAppBar,
  Box,
  Container,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useScrollTrigger,
  ListItem,
  Button,
  AppBar,
  Collapse
} from '@mui/material';
import {
  IconBook,
  IconChevronDown,
  IconChevronUp,
  IconCode,
  IconCpu,
  IconDashboard,
  IconDeviceDesktopAnalytics,
  IconHelpHexagon,
  IconHome2,
  IconMessages,
  IconPhoneCall
} from '@tabler/icons-react';
import MenuIcon from '@mui/icons-material/Menu';
import { useIntl } from 'react-intl';
import ModeSection from './ModeSection';
import LocalizationSection from './LocalizationSection';
import { useRouter } from 'next/router';
import Link from 'next/link';
import LogoSection from '../LogoSection';
import HoverPopover from 'material-ui-popup-state/HoverPopover';
import { usePopupState, bindHover, bindPopover } from 'material-ui-popup-state/hooks';
import ContactSection from './ContactSection';
import CareerSection from './CareerSection';
import useConfig from 'hooks/useConfig';
import SupportSection from './SupportSection';
import ServicesSection from './ServicesSection';
import AboutSection from './AboutSection';
import ProductsSection from './ProductsSection';
import Image from 'next/image';
import { gtagReportConversion } from 'utils/gtag';

interface ElevationScrollProps {
  children: ReactElement<any, typeof AppBar>;
  window?: Window | Node;
}

interface CustomLinkItemButtonProps {
  icon: React.ReactElement;
  text: string;
  href: string;
  onClick?: () => void;
  endIcon?: React.ReactNode;
}

const logo = '/assets/images/about/mobile-logo.svg';
const logoDark = '/assets/images/about/mobile-logo-dark.svg';

function ElevationScroll({ children, window }: ElevationScrollProps) {
  const theme = useTheme();
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 0,
    target: window ?? undefined
  });

  return cloneElement(children, {
    sx: {
      ...(children.props.sx || {}),
      boxShadow: trigger ? theme.shadows[1] : 'none',
      backgroundColor: theme.palette.mode === 'dark' ? theme.palette.dark[800] : theme.palette.grey[50],
      color: theme.palette.text.dark
    }
  });
}

const Header = ({ ...others }) => {
  const router = useRouter();
  const theme = useTheme();
  const { locale } = useConfig();
  const { formatMessage } = useIntl();

  const [drawerToggle, setDrawerToggle] = useState(false);
  const [hoveredLink, setHoveredLink] = useState<string | null>(null);
  const [productsOpen, setProductsOpen] = useState(false);

  useEffect(() => {
    setDrawerToggle(false);
  }, [theme.palette.mode, locale]);

  const popupAboutState = usePopupState({
    variant: 'popover',
    popupId: 'aboutPopover'
  });

  const popupProductsState = usePopupState({
    variant: 'popover',
    popupId: 'productPopover'
  });

  const popupServicesState = usePopupState({
    variant: 'popover',
    popupId: 'servicesPopover'
  });

  const popupSupportState = usePopupState({
    variant: 'popover',
    popupId: 'supportPopover'
  });

  const popupCareerState = usePopupState({
    variant: 'popover',
    popupId: 'careerPopover'
  });

  const popupContactState = usePopupState({
    variant: 'popover',
    popupId: 'contactPopover'
  });

  const drawerToggler = (open: boolean) => (event: any) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setDrawerToggle(open);
  };

  const handleMouseEnter = (link: string) => {
    setHoveredLink(link);
  };

  const handleMouseLeave = () => {
    setHoveredLink(null);
  };

  const handleAboutPopoverClose = () => {
    popupAboutState.close();
  };

  const handleProductsPopoverClose = () => {
    popupProductsState.close();
  };

  const handleServicesPopoverClose = () => {
    popupServicesState.close();
  };

  const handleSupportPopoverClose = () => {
    popupSupportState.close();
  };

  const handleCareerPopoverClose = () => {
    popupCareerState.close();
  };

  const handleContactPopoverClose = () => {
    popupContactState.close();
  };

  const CustomLinkItemButton: React.FC<CustomLinkItemButtonProps> = ({ icon, text, href, onClick, endIcon }) => (
    <ListItem disablePadding>
      <ListItemButton component={Link} href={href} onClick={onClick}>
        <ListItemIcon>{icon}</ListItemIcon>
        <ListItemText primary={text} />
        {endIcon && <ListItemIcon>{endIcon}</ListItemIcon>}
      </ListItemButton>
    </ListItem>
  );

  return (
    <ElevationScroll {...others}>
      <MuiAppBar>
        <Container>
          <Toolbar sx={{ padding: '0px !important' }}>
            <Typography
              component="div"
              sx={{
                flexGrow: 1,
                textAlign: 'left',
                pt: '22.0px',
                pb: '22.5px',
                px: `0 !important`,
                alignItems: 'center',
                alignSelf: 'center',
                display: 'flex'
              }}
            >
              <LogoSection />
            </Typography>
            <Box
              sx={{
                display: { xs: 'none', md: 'flex' },
                '& > *': { paddingLeft: { xs: '12px', lg: '18px' }, paddingRight: { xs: '12px', lg: '18px' } }
              }}
            >
              <Box {...bindHover(popupAboutState)} sx={{ paddingTop: 4, paddingBottom: 4 }}>
                <Typography
                  variant="h1"
                  sx={{
                    color: router.pathname === '/about' || hoveredLink === '/about' ? '#FFA350' : 'inherit',
                    fontSize: 16,
                    fontWeight: 500,
                    cursor: 'pointer'
                  }}
                  onMouseEnter={() => handleMouseEnter('/about')}
                  onMouseLeave={handleMouseLeave}
                >
                  <Link href="/about">{formatMessage({ id: 'ABOUT' })}</Link>
                </Typography>
              </Box>

              <Box {...bindHover(popupProductsState)} sx={{ paddingTop: 4, paddingBottom: 4 }}>
                <Typography
                  variant="h1"
                  sx={{
                    color: router.pathname === '/products' || hoveredLink === '/products' ? '#FFA350' : 'inherit',
                    fontSize: 16,
                    fontWeight: 500,
                    cursor: 'pointer'
                  }}
                  onMouseEnter={() => handleMouseEnter('/products')}
                  onMouseLeave={handleMouseLeave}
                >
                  <Link href="/products">{formatMessage({ id: 'OUR.PRODUCTS' })}</Link>
                </Typography>
              </Box>

              <Box {...bindHover(popupServicesState)} sx={{ paddingTop: 4, paddingBottom: 4 }}>
                <Typography
                  variant="h1"
                  sx={{
                    color: router.pathname === '/services' || hoveredLink === '/services' ? '#FFA350' : 'inherit',
                    fontSize: 16,
                    fontWeight: 500,
                    cursor: 'pointer'
                  }}
                  onMouseEnter={() => handleMouseEnter('/services')}
                  onMouseLeave={handleMouseLeave}
                >
                  <Link href="/services">{formatMessage({ id: 'OUR.SERVICES' })}</Link>
                </Typography>
              </Box>

              <Box {...bindHover(popupSupportState)} sx={{ paddingTop: 4, paddingBottom: 4 }}>
                <Typography
                  variant="h1"
                  sx={{
                    color: router.pathname === '/support' || hoveredLink === '/support' ? '#FFA350' : 'inherit',
                    fontSize: 16,
                    fontWeight: 500,
                    cursor: 'pointer'
                  }}
                  onMouseEnter={() => handleMouseEnter('/support')}
                  onMouseLeave={handleMouseLeave}
                >
                  <Link href="/support">{formatMessage({ id: 'SUPPORT' })}</Link>
                </Typography>
              </Box>

              <Box {...bindHover(popupCareerState)} sx={{ paddingTop: 4, paddingBottom: 4 }}>
                <Typography
                  variant="h1"
                  sx={{
                    color: router.pathname === '/career' || hoveredLink === '/career' ? '#FFA350' : 'inherit',
                    fontSize: 16,
                    fontWeight: 500,
                    cursor: 'pointer'
                  }}
                  onMouseEnter={() => handleMouseEnter('/career')}
                  onMouseLeave={handleMouseLeave}
                >
                  <Link href="/career">{formatMessage({ id: 'CAREER' })}</Link>
                </Typography>
              </Box>

              <Box {...bindHover(popupContactState)} sx={{ paddingTop: 4, paddingBottom: 4 }}>
                <Typography
                  variant="h1"
                  sx={{
                    color: router.pathname === '/contact' || hoveredLink === '/contact' ? '#FFA350' : 'inherit',
                    fontSize: 16,
                    fontWeight: 500,
                    cursor: 'pointer'
                  }}
                  onMouseEnter={() => handleMouseEnter('/contact')}
                  onMouseLeave={handleMouseLeave}
                >
                  <Link href="/contact">{formatMessage({ id: 'CONTACT' })}</Link>
                </Typography>
              </Box>
              <Box sx={{ paddingTop: 3, paddingBottom: 3 }}>
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: 16,
                    fontWeight: 500,
                    cursor: 'pointer',
                    '& a': { textDecoration: 'none', color: 'white' }
                  }}
                  onMouseEnter={() => handleMouseEnter('/contact-us')}
                  onMouseLeave={handleMouseLeave}
                >
                  <Button
                    component={Link}
                    href="/contact-us"
                    disableElevation
                    variant="contained"
                    sx={{
                      backgroundColor: '#EA7100',
                      '&:hover': { backgroundColor: '#EA7100' },
                      '&.MuiButton-root': { transition: 'none' }
                    }}
                    onClick={() => gtagReportConversion(undefined, 'AW-370956326/rhnPCP6nmbYaEKaw8bAB')}
                  >
                    {formatMessage({ id: 'CONTACT.US' })}
                  </Button>
                </Typography>
              </Box>
            </Box>

            <HoverPopover
              {...bindPopover(popupAboutState)}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center'
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'center'
              }}
              marginThreshold={0}
              sx={{
                '& .MuiPopover-paper': {
                  width: '100vw',
                  maxWidth: '100vw',
                  left: '0px',
                  right: '0px',
                  borderRadius: '0px'
                }
              }}
              disableScrollLock={true}
            >
              <AboutSection handlePopoverClose={handleAboutPopoverClose} />
            </HoverPopover>

            <HoverPopover
              {...bindPopover(popupProductsState)}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center'
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'center'
              }}
              marginThreshold={0}
              sx={{
                '& .MuiPopover-paper': {
                  width: '100vw',
                  maxWidth: '100vw',
                  left: '0px',
                  right: '0px',
                  borderRadius: '0px'
                }
              }}
              disableScrollLock={true}
            >
              <ProductsSection handlePopoverClose={handleProductsPopoverClose} />
            </HoverPopover>

            <HoverPopover
              {...bindPopover(popupServicesState)}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center'
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'center'
              }}
              marginThreshold={0}
              sx={{
                '& .MuiPopover-paper': {
                  width: '100vw',
                  maxWidth: '100vw',
                  left: '0px',
                  right: '0px',
                  borderRadius: '0px'
                }
              }}
              disableScrollLock={true}
            >
              <ServicesSection handlePopoverClose={handleServicesPopoverClose} />
            </HoverPopover>

            <HoverPopover
              {...bindPopover(popupSupportState)}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center'
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'center'
              }}
              marginThreshold={0}
              sx={{
                '& .MuiPopover-paper': {
                  width: '100vw',
                  maxWidth: '100vw',
                  left: '0px',
                  right: '0px',
                  borderRadius: '0px'
                }
              }}
              disableScrollLock={true}
            >
              <SupportSection handlePopoverClose={handleSupportPopoverClose} />
            </HoverPopover>

            <HoverPopover
              {...bindPopover(popupContactState)}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center'
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'center'
              }}
              marginThreshold={0}
              sx={{
                '& .MuiPopover-paper': {
                  width: '100vw',
                  maxWidth: '100vw',
                  left: '0px',
                  right: '0px',
                  borderRadius: '0px'
                }
              }}
              disableScrollLock={true}
            >
              <ContactSection handlePopoverClose={handleContactPopoverClose} />
            </HoverPopover>

            <HoverPopover
              {...bindPopover(popupCareerState)}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center'
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'center'
              }}
              marginThreshold={0}
              sx={{
                '& .MuiPopover-paper': {
                  width: '100vw',
                  maxWidth: '100vw',
                  left: '0px',
                  right: '0px',
                  borderRadius: '0px'
                }
              }}
              disableScrollLock={true}
            >
              <CareerSection handlePopoverClose={handleCareerPopoverClose} />
            </HoverPopover>

            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
              <IconButton color="inherit" onClick={drawerToggler(true)} size="large">
                <MenuIcon />
              </IconButton>
              <Drawer anchor="top" open={drawerToggle} onClose={drawerToggler(false)}>
                {drawerToggle && (
                  <Box sx={{ width: 'auto' }} role="presentation">
                    <List>
                      <CustomLinkItemButton icon={<IconHome2 />} href="/home" text={formatMessage({ id: 'HOME' })} />
                      <CustomLinkItemButton
                        icon={<Image width={24} height={24} src={theme.palette.mode === 'dark' ? logoDark : logo} alt="Andevos" />}
                        href="/about"
                        text={formatMessage({ id: 'ABOUT' })}
                      />
                      <CustomLinkItemButton
                        icon={<IconBook />}
                        href="#"
                        text={formatMessage({ id: 'OUR.PRODUCTS' })}
                        onClick={() => setProductsOpen(!productsOpen)}
                        endIcon={productsOpen ? <IconChevronUp size={18} /> : <IconChevronDown size={18} />}
                      />

                      <Collapse in={productsOpen} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding sx={{ pl: 4 }}>
                          <CustomLinkItemButton
                            icon={<IconCpu />}
                            href="/hardware-products"
                            text={formatMessage({ id: 'HARDWARE.PRODUCTS' })}
                            onClick={() => {
                              setDrawerToggle(false);
                              setProductsOpen(false);
                            }}
                          />
                          <CustomLinkItemButton
                            icon={<IconCode />}
                            href="/software-products"
                            text={formatMessage({ id: 'SOFTWARE.PRODUCTS' })}
                            onClick={() => {
                              setDrawerToggle(false);
                              setProductsOpen(false);
                            }}
                          />
                        </List>
                      </Collapse>
                      <CustomLinkItemButton
                        icon={<IconDeviceDesktopAnalytics />}
                        href="/services"
                        text={formatMessage({ id: 'OUR.SERVICES' })}
                      />
                      <CustomLinkItemButton icon={<IconHelpHexagon />} href="/support" text={formatMessage({ id: 'SUPPORT' })} />
                      <CustomLinkItemButton icon={<IconDashboard />} href="/career" text={formatMessage({ id: 'CAREER' })} />
                      <CustomLinkItemButton href="/contact" icon={<IconPhoneCall />} text={formatMessage({ id: 'CONTACT' })} />
                      <CustomLinkItemButton href="/contact-us" icon={<IconMessages />} text={formatMessage({ id: 'CONTACT.US' })} />
                      <ListItem>
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                          <ModeSection />
                          <LocalizationSection />
                        </Box>
                      </ListItem>
                    </List>
                  </Box>
                )}
              </Drawer>
            </Box>

            <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'block' } }} />
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <ModeSection />
            </Box>

            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <LocalizationSection />
            </Box>
          </Toolbar>
        </Container>
      </MuiAppBar>
    </ElevationScroll>
  );
};

export default Header;
